// src/features/locker-assignments/components/TagsPopover.tsx

import React, { useState } from 'react';
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  Button,
  CircularProgress,
  List,
  ListItemButton,
  Collapse,
} from '@mui/material';
import {
  Receipt as TagIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  ContentCopy as CopyIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useTranslate, useNotify, useDataProvider, useRefresh } from 'react-admin';
import QRCodeDisplay from './QRCodeDisplay';
import ManageTagsDialog from './ManageTagsDialog';

// Actualizar interfaces para el nuevo formato
interface Tag {
  id: string;
  typeCode: string;
  typeName?: string;
  data: string;
  code?: string; // Para compatibilidad
  description?: string;
  isActive: boolean;
  individualQuota?: number;
  status?: 'Active' | 'Inactive'; // Para compatibilidad con ManageTagsDialog
}

interface AssignmentData {
  id: string;
  hotelId: string;
  quotaPolicy?: 'Individual' | 'Shared';
  displayReference: string;
  tags: Tag[];
}

interface TagsPopoverProps {
  assignmentId: string;
  assignmentReference: string;
  tagsCount: number;
  hotelId: string;
  assignmentStatus?: string; 
}

export const TagsPopover: React.FC<TagsPopoverProps> = ({
  assignmentId,
  assignmentReference,
  tagsCount,
  hotelId,
  assignmentStatus = 'Active', // Valor por defecto
}) => {
  
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [tags, setTags] = useState<Tag[]>([]);
  const [assignmentData, setAssignmentData] = useState<AssignmentData | null>(null);
  const [loading, setLoading] = useState(false);
  const [expandedTag, setExpandedTag] = useState<string | null>(null);
  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  const open = Boolean(anchorEl);
  const id = open ? 'tags-popover' : undefined;
  
  // NUEVO: Verificar si la asignación está archivada
  const isArchived = assignmentStatus === 'Archived';
  const canManageTags = !isArchived;

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    // NUEVO: Si está archivada, mostrar mensaje
    if (isArchived) {
      notify(translate('resources.locker-assignments.tags.archived_no_edit'), { type: 'warning' });
      return;
    }
    
    setAnchorEl(event.currentTarget);
    
    if (!tags.length && tagsCount > 0) {
      setLoading(true);
      try {
        const { data } = await dataProvider.getOne('locker-assignments', {
          id: assignmentId,
        });
        
        // Solo necesitamos los tags, el hotelId ya lo tenemos
        setAssignmentData({
          id: data.id,
          hotelId: hotelId,
          quotaPolicy: data.quotaPolicy,
          displayReference: data.displayReference || assignmentReference,
          tags: data.tags || [],
        });
        
        if (data.tags && Array.isArray(data.tags)) {
          // Normalizar los tags para asegurar compatibilidad
          const normalizedTags = data.tags.map((tag: any) => ({
            ...tag,
            code: tag.code || tag.data, // Asegurar que code existe
            status: tag.isActive ? 'Active' : 'Inactive', // Agregar status para compatibilidad
          }));
          setTags(normalizedTags);
        }
      } catch (error) {
        console.error('Error loading assignment details:', error);
        notify(translate('resources.locker-assignments.tags.loading_error'), { type: 'error' });
      } finally {
        setLoading(false);
      }
    }
  };

  // Busca la función handleManageTags y después de ella añade:

const handleManageDialogClose = async () => {
  setManageDialogOpen(false);
  
  // Recargar tags desde el servidor para obtener los códigos generados
  await reloadTagsFromServer();
  
  // Luego refrescar la lista principal
  refresh();
};

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleTagClick = (tagId: string) => {
    setExpandedTag(expandedTag === tagId ? null : tagId);
  };

  const handleCopyTag = (code: string, event: React.MouseEvent) => {
    event.stopPropagation();
    navigator.clipboard.writeText(code).then(() => {
      notify(translate('resources.locker-assignments.tags.code_copied'), { type: 'success' });
    }).catch((error) => {
      console.error('Error copying code:', error);
      notify(translate('resources.locker-assignments.tags.error_copying'), { type: 'error' });
    });
  };

  const handleManageTags = () => {
    setManageDialogOpen(true);
    handleClose();
  };


  const reloadTagsFromServer = async () => {
  setLoading(true);
  try {
    const { data } = await dataProvider.getOne('locker-assignments', {
      id: assignmentId,
    });
    
    setAssignmentData({
      id: data.id,
      hotelId: hotelId,
      quotaPolicy: data.quotaPolicy,
      displayReference: data.displayReference || assignmentReference,
      tags: data.tags || [],
    });
    
    if (data.tags && Array.isArray(data.tags)) {
      const normalizedTags = data.tags.map((tag: any) => ({
        ...tag,
        code: tag.code || tag.data,
        status: tag.isActive ? 'Active' : 'Inactive',
      }));
      setTags(normalizedTags);
    }
  } catch (error) {
    console.error('Error reloading tags:', error);
    notify(translate('resources.locker-assignments.tags.loading_error'), { type: 'error' });
  } finally {
    setLoading(false);
  }
};


const handleTagsUpdated = async (updatedTags: any[]) => {
  // Verificar si hay tags sin código (autogenerados)
  const hasAutoGeneratedTags = updatedTags.some(tag => !tag.code && !tag.data);
  
  if (hasAutoGeneratedTags) {
    // Si hay tags autogenerados, recargar desde el servidor
    await reloadTagsFromServer();
  } else {
    // Si no, actualizar localmente
    const normalizedTags = updatedTags.map(tag => ({
      ...tag,
      isActive: tag.status === 'Active',
      data: tag.data || tag.code,
    }));
    setTags(normalizedTags);
  }
};

  // Convertir tags al formato esperado por ManageTagsDialog
  const tagsForDialog = tags.map(tag => ({
    id: tag.id,
    code: tag.code || tag.data,
    data: tag.data,
    typeCode: tag.typeCode,
    typeName: tag.typeName,
    description: tag.description,
    status: (tag.status || (tag.isActive ? 'Active' : 'Inactive')) as 'Active' | 'Inactive',
    createdAt: undefined,
  }));

  return (
    <>
      <IconButton
        size="small"
        onClick={handleClick}
        aria-describedby={id}
        disabled={isArchived} 
        sx={{ 
          border:'none',
          color: !isArchived 
            ? (tagsCount > 0 ? 'primary.main' : 'action.active')
            : 'action.disabled',
        }}
      >
       <Badge
          badgeContent={tagsCount}
          color="primary"
          max={99}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          
        >
          <TagIcon fontSize="small" />
        </Badge>

      </IconButton>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        slotProps={{
          paper: {
            sx: { 
              backgroundColor: 'grey.50',
              maxWidth: 400, 
              minWidth: 320,
              maxHeight: 600,
              boxShadow: 3,
              overflow: 'auto'
            }
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* Header */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 2 
          }}>
            <Typography variant="subtitle2" color="text.secondary">
              {assignmentReference}
            </Typography>
            
            {canManageTags && (
              <Button
                size="small"
                startIcon={tags.length > 0 ? <SettingsIcon /> : <AddIcon />}
                onClick={handleManageTags}
                sx={{ ml: 2 }}
              >
                {tags.length > 0 
                  ? translate('resources.locker-assignments.tags.manage')
                  : translate('resources.locker-assignments.tags.add_tags') // Cambiar texto cuando no hay tags
                }
              </Button>
            )}
          </Box>

 

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : tagsCount === 0 ? (
            <Box sx={{ py: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {translate('resources.locker-assignments.tags.no_tags_message')}
              </Typography>
             
            </Box>
          ) : tags.length === 0 ? (
            <Box sx={{ py: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {translate('resources.locker-assignments.tags.loading_error')}
              </Typography>
            </Box>
          ) : (
            <List dense sx={{ py: 0 }}>
              {tags.map((tag) => {
                const isExpanded = expandedTag === tag.id;

                return (
                  <Box key={tag.id}>
                    {/* Tag Header - Always visible */}
                    <ListItemButton
                      onClick={() => handleTagClick(tag.id)}
                      sx={{ 
                        borderRadius: 2,
                        mb: 1,
                        p: 1.5,
                        border: '1px solid',
                        borderColor: isExpanded ? 'grey.300' : 'grey.200',
                        backgroundColor: isExpanded ? 'white' : 'transparent',
                        boxShadow: isExpanded 
                          ? '0 2px 8px rgba(0,0,0,0.08)' 
                          : '0 1px 2px rgba(0,0,0,0.04)',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          backgroundColor: 'white',
                          boxShadow: '0 3px 10px rgba(0,0,0,0.12)',
                          borderColor: 'grey.400'
                        }
                      }}
                    >
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        width: '100%',
                        minHeight: 24
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, mr: 2 }}>
                          <Typography 
                            variant="body2"
                            sx={{ 
                              fontFamily: 'monospace',
                              letterSpacing: '2px',
                              fontSize: '0.9rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                         

                            {tag.data}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {/* Expand icon */}
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            backgroundColor: isExpanded ? 'primary.main' : 'grey.200',
                            transition: 'all 0.2s ease-in-out'
                          }}>
                            <ExpandMoreIcon 
                              sx={{ 
                                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.25s ease-in-out',
                                color: isExpanded ? 'white' : 'text.secondary',
                                fontSize: 18
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </ListItemButton>

                    {/* Tag Content - Expandible */}
                    <Collapse in={isExpanded} timeout={200}>
                      <Box sx={{ pl: 2, pr: 1, pb: 2 }}>
                        {/* Mostrar descripción si existe */}
                        {tag.description && (
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                            {tag.description}
                          </Typography>
                        )}

                        {/* QR Display for AUTO_NUMERIC tags */}
                        {tag.typeCode === 'AUTO_NUMERIC' ? (
                          <QRCodeDisplay 
                            code={tag.data}
                            displayReference={assignmentReference}
                            tagId={tag.id}
                            assignmentId={assignmentData?.id}
                            hotelId={assignmentData?.hotelId}
                          />
                        ) : (
                          /* Text display for other tag types */
                          <Box sx={{ 
                            backgroundColor: 'grey.50', 
                            borderRadius: 1, 
                            p: 1.5,
                            position: 'relative'
                          }}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontFamily: 'monospace',
                                wordBreak: 'break-all',
                                pr: 4 
                              }}
                            >
                              {tag.data}
                            </Typography>
                            
                            <IconButton
                              size="small"
                              onClick={(e) => handleCopyTag(tag.data, e)}
                              sx={{ 
                                position: 'absolute', 
                                top: 8, 
                                right: 8 
                              }}
                            >
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    </Collapse>
                  </Box>
                );
              })}
            </List>
          )}
        </Box>
      </Popover>

      {manageDialogOpen && (() => {
        return (
          <ManageTagsDialog
            open={manageDialogOpen}
            onClose={handleManageDialogClose}
            assignmentId={assignmentId}
            assignmentReference={assignmentReference}
            currentTags={tagsForDialog}
            onTagsUpdated={handleTagsUpdated}
            hotelId={hotelId}
          />
        );
      })()}
    </>
  );
};

export default TagsPopover;